import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldSize,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import * as askRedoStyles from "./ask-redo-button.module.css";
import * as css from "./feedback-callout.module.css";

const FEEDBACK_MAX_LENGTH = 500;

/**
 * Step in the feedback flow. After the first comment is submitted the backend
 * may return an AI-generated follow-up question (`followUp`); otherwise we go
 * straight to the final thank-you (`done`).
 */
type FeedbackStep = "feedback" | "followUp" | "done";

/**
 * Fallback follow-up header if the backend signals a follow-up is needed but
 * (defensively) returns no question text.
 */
const DEFAULT_FOLLOW_UP_QUESTION =
  "Got it — what were you trying to do when this came up?";

/**
 * If the merchant lands on the follow-up step but goes idle for this long, we
 * flush their feedback to Slack without an answer rather than lose it.
 */
const FOLLOW_UP_INACTIVITY_TIMEOUT_MS = 60_000;

export interface FeedbackCalloutProps {
  onClose?: () => void;
}

const OrangeAiStarIcon = () => <AiStarIcon className={askRedoStyles.icon} />;

export const FeedbackCallout = memo(function FeedbackCallout({
  onClose,
}: FeedbackCalloutProps) {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const { open: openAskRedoPanel } = useAskRedoSlidePanel();
  const [step, setStep] = useState<FeedbackStep>("feedback");
  const [comment, setComment] = useState("");
  // The original feedback comment (preserved for the Ask Redo prefill once the
  // textarea is reused for the follow-up answer).
  const [feedbackComment, setFeedbackComment] = useState("");
  // Set from the first submit's response so the follow-up answer can be
  // attached to the persisted row, and the real AI question can be shown.
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  const [followUpQuestion, setFollowUpQuestion] = useState<string | null>(null);
  // The merchant's submitted answer to the follow-up question, preserved so the
  // Ask Redo prefill can include the full feedback → question → answer thread.
  const [followUpAnswer, setFollowUpAnswer] = useState("");

  // While a follow-up is outstanding the backend holds the Slack notification
  // until we call submitMerchantFeedbackFollowUp. This ref holds the id that
  // still needs flushing so that closing the popover or going idle doesn't
  // strand the feedback. It is cleared the moment we send an answer or flush.
  const pendingFollowUpRef = useRef<string | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // Send the feedback to Slack without an answer (an empty follow-up call is a
  // "skip" on the backend). Idempotent via the ref so close + timeout can't
  // double-send.
  const flushPendingFollowUp = useCallback(() => {
    const id = pendingFollowUpRef.current;
    if (!id) {
      return;
    }
    pendingFollowUpRef.current = null;
    void platformRpcClient
      .submitMerchantFeedbackFollowUp({ feedbackId: id })
      .catch(() => {
        // Best-effort flush on close/timeout; nothing to surface to the user.
      });
  }, [platformRpcClient]);

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();
    inactivityTimerRef.current = setTimeout(() => {
      inactivityTimerRef.current = null;
      flushPendingFollowUp();
      setStep("done");
    }, FOLLOW_UP_INACTIVITY_TIMEOUT_MS);
  }, [clearInactivityTimer, flushPendingFollowUp]);

  // Flush on unmount — closing the popover (click-off, escape, toggling the
  // button) unmounts this component, so this is what catches the "clicked
  // away" case.
  useEffect(() => {
    return () => {
      clearInactivityTimer();
      flushPendingFollowUp();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [submitLoad, handleSubmit] = useTriggerLoad(async () => {
    const trimmed = comment.trim();
    if (!trimmed) {
      return;
    }

    if (step === "feedback") {
      const pageUrl = `${window.location.pathname}${window.location.search}`;
      const result = await platformRpcClient.submitMerchantFeedback({
        comment: trimmed,
        pageUrl,
      });
      setFeedbackComment(trimmed);

      // The backend's LLM decides whether a follow-up is warranted. Only show
      // the follow-up step when it asks one; otherwise finish.
      if (result.needsFollowUp && result.feedbackId) {
        pendingFollowUpRef.current = result.feedbackId;
        setFeedbackId(result.feedbackId);
        setFollowUpQuestion(result.followUpQuestion ?? null);
        setStep("followUp");
        setComment("");
        startInactivityTimer();
      } else {
        setStep("done");
      }
      return;
    }

    // Follow-up step: attach the answer to the existing feedback row rather
    // than creating a new one. Clear the pending ref first so the unmount /
    // timeout flush can't also fire and double-send to Slack.
    clearInactivityTimer();
    pendingFollowUpRef.current = null;
    setFollowUpAnswer(trimmed);
    if (feedbackId) {
      try {
        await platformRpcClient.submitMerchantFeedbackFollowUp({
          feedbackId,
          answer: trimmed,
        });
      } catch (error) {
        // The deferred Slack notification only fires once this call succeeds.
        // If it fails, restore the pending ref so a retry — or the unmount /
        // close flush — can still deliver the feedback to Slack rather than
        // stranding the persisted row with no notification. The timer stays
        // cleared, so this can't double-send on the normal path.
        pendingFollowUpRef.current = feedbackId;
        throw error;
      }
    }
    setStep("done");
  });

  const submitting = submitLoad.pending;
  const error =
    !submitting && submitLoad.error
      ? "Something went wrong. Please try again."
      : null;

  const handleAsk = () => {
    // Hand Ask Redo the whole thread — the original feedback plus the follow-up
    // question and the merchant's answer when one was asked — so the concierge
    // has the full context, not just the first comment.
    const segments = [feedbackComment.trim() || comment.trim()];
    if (followUpQuestion?.trim()) {
      segments.push(followUpQuestion.trim());
      if (followUpAnswer.trim()) {
        segments.push(followUpAnswer.trim());
      }
    }
    const conversation = segments.filter(Boolean).join("\n\n");
    onClose?.();
    openAskRedoPanel({
      kind: "prefill",
      input: conversation,
      autoSubmit: true,
    });
  };

  const handleSetComment = (next: string) => {
    setComment(next.slice(0, FEEDBACK_MAX_LENGTH));
    // Typing on the follow-up step counts as activity — push back the
    // idle-flush so we don't cut off a merchant mid-answer.
    if (step === "followUp") {
      startInactivityTimer();
    }
  };

  return (
    <Flex
      bgColor="primary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      className={css.container}
      dir="column"
      gap="xl"
      p="xl"
      radius="xl"
      shadow="lg"
    >
      {step === "done" ? (
        <>
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              Thank you for your feedback!
            </Text>
            <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
              We've received your note and our team will look into it. See{" "}
              <a
                className={css.tosLink}
                data-track-id="merchant.top-nav.feedback.common-questions"
                href="https://help.getredo.com/en/"
                rel="noopener noreferrer"
                target="_blank"
              >
                common questions
              </a>{" "}
              for additional support.
            </Text>
          </Flex>
          <Flex align="center" gap="md" justify="flex-end">
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              Continue conversation?
            </Text>
            <RedoButton
              dataTrackId="merchant.top-nav.feedback.ask"
              hierarchy="secondary"
              IconLeading={OrangeAiStarIcon}
              onClick={handleAsk}
              size="sm"
              text="Yes"
            />
          </Flex>
        </>
      ) : (
        <>
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="semibold" textColor="primary">
              {step === "feedback"
                ? "Give feedback"
                : (followUpQuestion ?? DEFAULT_FOLLOW_UP_QUESTION)}
            </Text>
            <RedoTextareaInputField
              containerHeight="var(--redo-spacing-8xl)"
              placeholder={
                step === "feedback"
                  ? "What's working? What's not?"
                  : "Provide more context here"
              }
              setValue={handleSetComment}
              size={RedoTextareaInputFieldSize.SMALL}
              value={comment}
            />
          </Flex>
          {error && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              {error}
            </Text>
          )}
          <Flex align="center" gap="md" justify="space-between">
            <Text fontSize="xxs" fontWeight="regular" textColor="tertiary">
              Feedback provided is subject to your existing services agreement
              or Redo's{" "}
              <a
                className={css.tosLink}
                href="https://redo.com/terms-and-conditions"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms of Service
              </a>
              .
            </Text>
            <RedoButton
              dataTrackId="merchant.top-nav.feedback.submit"
              disabled={comment.trim() === "" || submitting}
              hierarchy="primary"
              onClick={() => handleSubmit()}
              size="sm"
              text={submitting ? "Submitting..." : "Submit"}
            />
          </Flex>
        </>
      )}
    </Flex>
  );
});
